import { memo, ReactNode, useEffect, useRef, useState } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

import Autocomplete from '@mui/material/Autocomplete';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useGetDaasEntities } from '../../../../hooks';
import { DAAS_APPLICATION_TYPE } from '../../../../types';
import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import {
  APPLICATION_DESCRIPTION_INFO,
  APPLICATION_NAME_INFO,
} from '../infoDoc';
import { useStyles } from '../styles';
import { DaasApplicationInputData } from '../types';
import { convertEntityToApplicationInput } from '../utils';

const DESCRIPTION_MAX_LENGTH = 250;

export interface ApplicationEditBasicInfoValue {
  applicationName: string;
  description: string;
  applicationRef?: string;
}

export interface ApplicationEditBasicInfoSectionProps {
  value: ApplicationEditBasicInfoValue;
  onChange: (value: ApplicationEditBasicInfoValue) => void;
  onApplicationEntitySelect: (data: Partial<DaasApplicationInputData>) => void;
  isFormAlreadyPopulated?: boolean;
  errors?: string[];
}

const findApplicationEntity = (
  entities: Entity[],
  applicationRef: string | undefined,
): Entity | null => {
  if (applicationRef) {
    return entities.find(e => stringifyEntityRef(e) === applicationRef) ?? null;
  }
  return null;
};

const ApplicationEditBasicInfoSectionInner = ({
  value,
  onChange,
  onApplicationEntitySelect,
  isFormAlreadyPopulated = false,
  errors = [],
}: ApplicationEditBasicInfoSectionProps) => {
  const { classes } = useStyles();
  const { value: applicationEntities = [], loading: applicationsLoading } =
    useGetDaasEntities(DAAS_APPLICATION_TYPE, false, true);

  const selectedEntity =
    value.applicationName || value.applicationRef
      ? findApplicationEntity(applicationEntities, value.applicationRef)
      : null;

  const [inaccessibleApplicationName, setInaccessibleApplicationName] =
    useState<string | null>(null);
  const hasAutoPrefilled = useRef(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  // Stable refs — keeps the effect deps free of the callbacks without stale
  // closures. Always read .current inside callbacks instead of closing over the
  // prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onApplicationEntitySelectRef = useRef(onApplicationEntitySelect);
  onApplicationEntitySelectRef.current = onApplicationEntitySelect;

  const applicationNotAccessible =
    Boolean(inaccessibleApplicationName) ||
    (errors.length > 0 && !value.applicationName);

  useEffect(() => {
    if (applicationNotAccessible) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [applicationNotAccessible]);

  // Auto-prefill when arriving via URL with a pre-set application ref.
  // Skipped once the form has been populated (back-navigation or field edits)
  // to prevent overwriting user changes.
  useEffect(() => {
    if (
      (!value.applicationName && !value.applicationRef) ||
      applicationsLoading
    )
      return;
    const entity = findApplicationEntity(
      applicationEntities,
      value.applicationRef,
    );
    if (!entity) {
      setInaccessibleApplicationName(value.applicationName);
      return;
    }
    setInaccessibleApplicationName(null);
    if (hasAutoPrefilled.current || isFormAlreadyPopulated) return;
    hasAutoPrefilled.current = true;
    const fullData = convertEntityToApplicationInput(entity);
    onChangeRef.current({
      applicationName: fullData.applicationName || value.applicationName,
      description: fullData.description || '',
      applicationRef: stringifyEntityRef(entity),
    });
    onApplicationEntitySelectRef.current(fullData);
  }, [
    value.applicationName,
    value.applicationRef,
    applicationEntities,
    applicationsLoading,
    isFormAlreadyPopulated,
  ]); // callbacks excluded via refs

  const handleEntitySelect = (_: unknown, entity: Entity) => {
    setInaccessibleApplicationName(null);
    hasAutoPrefilled.current = true;
    const fullData = convertEntityToApplicationInput(entity);
    onChangeRef.current({
      applicationName: fullData.applicationName || entity.metadata.name,
      description: fullData.description || '',
      applicationRef: stringifyEntityRef(entity),
    });
    onApplicationEntitySelectRef.current(fullData);
  };

  let applicationHelperText: ReactNode;
  if (inaccessibleApplicationName) {
    applicationHelperText = (
      <ScaffolderFieldError
        message={`You do not have permission to edit application "${inaccessibleApplicationName}". Select a different application.`}
      />
    );
  } else if (applicationNotAccessible) {
    applicationHelperText = (
      <ScaffolderFieldError message="Application is required" />
    );
  }

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Application configuration"
          description="Select an application to edit its configuration."
          variant="h5"
          divider
        />
        <Backdrop
          open={applicationsLoading}
          className={classes.loadingProgress}
        >
          <CircularProgress size={48} />
        </Backdrop>
        <div ref={sectionRef}>
          <Box className={classes.inputContainer}>
            <ScaffolderInputHeader
              title="Application to edit"
              description="Select the application you want to edit."
              infoDoc={APPLICATION_NAME_INFO}
            />
            <Autocomplete
              size="small"
              // `disableClearable` narrows the value type to a non-null Entity,
              // but nothing is selected until the user picks an application.
              value={selectedEntity as Entity}
              loading={applicationsLoading}
              onChange={handleEntitySelect}
              options={applicationEntities}
              disableClearable
              getOptionLabel={option =>
                option.metadata.title || option.metadata.name
              }
              isOptionEqualToValue={(option, selected) =>
                stringifyEntityRef(option) === stringifyEntityRef(selected)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Application"
                  variant="outlined"
                  margin="none"
                  required
                  error={applicationNotAccessible}
                  helperText={applicationHelperText}
                />
              )}
              renderOption={({ key, ...optionProps }, option) => (
                <li key={key} {...optionProps}>
                  <Box>
                    <Typography variant="body2" noWrap>
                      {option.metadata.title || option.metadata.name}
                    </Typography>
                    {option.metadata.title && (
                      <Typography
                        variant="caption"
                        noWrap
                        color="text.secondary"
                      >
                        {option.metadata.name}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              noOptionsText={
                applicationsLoading
                  ? 'Loading applications...'
                  : 'No accessible applications found'
              }
              loadingText="Loading applications..."
            />
          </Box>
        </div>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Description"
            description="Specify a description of the application."
            infoDoc={APPLICATION_DESCRIPTION_INFO}
          />
          <TextField
            label="Description"
            value={value.description}
            onChange={e =>
              onChangeRef.current({ ...value, description: e.target.value })
            }
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={3}
            slotProps={{
              htmlInput: { maxLength: DESCRIPTION_MAX_LENGTH },
            }}
            helperText="Optional. Up to 250 characters."
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const ApplicationEditBasicInfoSection = memo(
  ApplicationEditBasicInfoSectionInner,
);
